import { useEffect, useId, useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { XIcon } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import cn from 'classnames';

import usePrefersReducedMotion from '@hooks/usePrefersReducedMotion';
import useObjectVisibility from '@hooks/useObjectVisibility';

import styles from './StaffQuote.module.scss';

/** How long the mobile trigger's "Staff advice" label stays peeked out once
    scrolled into view — same dwell as `ImageHotspot/ReviewPanel`, which
    implements the same Figma "Dynamic Peek" trigger. */
const PEEK_HOLD_MS = 2500;

export interface StaffQuoteProps {
  /** Paragraphs of the authored quote. */
  quote: string[];
  attribution: string;
}

const QuoteFigure = ({ quote, attribution }: StaffQuoteProps) => (
  <figure className={styles.figure}>
    <blockquote className={styles.quote}>
      {quote.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </blockquote>
    {attribution && (
      <figcaption className={styles.attribution}>{attribution}</figcaption>
    )}
  </figure>
);

/**
 * Staff quote over the Why Choose image. Desktop shows it beside the MEC
 * peak avatar at the bottom of the image; mobile collapses it behind a
 * "Staff advice" trigger that reveals it as a card (Figma 374:6631).
 */
const StaffQuote = ({ quote, attribution }: StaffQuoteProps) => {
  const { t } = useTranslation('pdp');
  const cardId = useId();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [open, setOpen] = useState(false);
  const [peeking, setPeeking] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  // Set once the peek has run (or the user opened the quote), so closing the
  // quote doesn't replay it — `isAnchorVisible` stays true after the observer
  // disconnects, so the effect below reruns on every open → closed change.
  const hasPeekedRef = useRef(false);
  const isAnchorVisible = useObjectVisibility(anchorRef, 0.5, true);

  // Peek the "Staff advice" label once when scrolled into view.
  useEffect(() => {
    if (!isAnchorVisible || open || hasPeekedRef.current) return undefined;
    setPeeking(true);
    const timer = window.setTimeout(() => {
      hasPeekedRef.current = true;
      setPeeking(false);
    }, PEEK_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [isAnchorVisible, open]);

  // Dismiss on a press outside the card/trigger, or on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (!anchorRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const card = <QuoteFigure quote={quote} attribution={attribution} />;

  return (
    <>
      <div className={styles.desktopPanel}>
        <span className={styles.peakAvatar} aria-hidden="true">
          <Image
            src="/images/mec-peak-icon.svg"
            width={16}
            height={16}
            alt=""
          />
        </span>
        <QuoteFigure quote={quote} attribution={attribution} />
      </div>

      <div className={styles.mobileAnchor} ref={anchorRef}>
        {prefersReducedMotion ? (
          open && (
            <div id={cardId} className={styles.mobileCard}>
              {card}
            </div>
          )
        ) : (
          <AnimatePresence>
            {open && (
              <motion.div
                id={cardId}
                className={styles.mobileCard}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.6 } }}
                exit={{ opacity: 0, y: 16, transition: { duration: 0.4 } }}
              >
                {card}
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* One button for both states so focus stays put across open/close. */}
        <button
          ref={triggerRef}
          type="button"
          className={cn(styles.mobileTrigger, {
            [styles.mobileTriggerPeeking]: !open && peeking,
          })}
          aria-expanded={open}
          aria-controls={cardId}
          aria-label={
            open
              ? t('whyChoose.closeQuoteAriaLabel')
              : t('whyChoose.expandQuoteAriaLabel')
          }
          onClick={() => {
            setOpen((wasOpen) => !wasOpen);
            hasPeekedRef.current = true;
            setPeeking(false);
          }}
        >
          {open ? (
            <XIcon size={16} aria-hidden weight="regular" />
          ) : (
            <>
              <Image
                src="/images/mec-peak-icon.svg"
                width={20}
                height={20}
                alt=""
                className={styles.peakIcon}
              />
              {peeking && (
                <span className={styles.mobileTriggerLabel} aria-hidden="true">
                  {t('whyChoose.staffAdviceLabel')}
                </span>
              )}
            </>
          )}
        </button>
      </div>
    </>
  );
};

export default StaffQuote;
